import { memo, useCallback, useMemo } from "react";
import { useFormikContext } from "formik";
import type { LeaderboardFormValues } from "../../LeaderboardEditable.types";
import IconButton from "@mui/material/IconButton";
import TrashIcon from "components/Icons/TrashIcon";
import LeaderboardHelpers from "features/Leaderboards/helpers/LeaderboardHelpers";
import Tooltip from "components/Tooltip";
import { useTranslation } from "react-i18next";
import useBoolean from "hooks/useBoolean";
import useLeaderboardsContext from "features/Leaderboards/hooks/useLeaderboardsContext";
import { useRequest } from "hooks";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";
import DeleteLeaderboardDialog from "./DeleteLeaderboardDialog";

interface DeleteLeaderboardActionProps {
  onCancelCreate(): void;
}

function DeleteLeaderboardAction(props: DeleteLeaderboardActionProps) {
  const { onCancelCreate } = props;
  const { t } = useTranslation();
  const { value: open, toggle } = useBoolean();
  const [, leaderboardActions] = useLeaderboardsContext();
  const deleteLeaderboard = useRequest(leaderboardActions.delete, true);

  const { values, isSubmitting } = useFormikContext<LeaderboardFormValues>();

  const handleDelete = useCallback(async () => {
    if (!values.id) {
      onCancelCreate();
      toggle();
      return;
    }
    await deleteLeaderboard(values.id);
    toggle();
  }, [values.id, deleteLeaderboard, toggle, onCancelCreate]);

  const name = useMemo<string | undefined>(() => {
    if (!values.name) return LeaderboardHelpers.getDefaultName(values);
    return values.name;
  }, [values]);

  return (
    <ThemeProvider2024>
      <Tooltip title={t("global.delete_button_text")} placement="top" arrow>
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={toggle}
            disabled={isSubmitting}
            aria-label={
              name
                ? t("leaderboard.config_panel_delete_leaderboard_aria_label", { name })
                : t("leaderboard.delete_dialog_header")
            }
          >
            <TrashIcon />
          </IconButton>
        </span>
      </Tooltip>
      <DeleteLeaderboardDialog name={name} onClose={toggle} open={open} onConfirm={handleDelete} />
    </ThemeProvider2024>
  );
}

export default memo(DeleteLeaderboardAction);
